import React, { useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Download, Upload, FileSpreadsheet, Users, Truck, BookOpen, FileText,
    FileCog, Receipt, DollarSign, Loader2, CheckCircle2, AlertCircle, Package,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import axios from "axios";

const ENTITIES = [
    { kind: "customers", name: "Customers", icon: Users,
      desc: "Contact list with billing info (Zoho Books Contacts format supported)." },
    { kind: "vendors", name: "Vendors", icon: Truck,
      desc: "Suppliers and payees with contact details." },
    { kind: "chart_of_accounts", name: "Chart of Accounts", icon: BookOpen,
      desc: "Account codes, names, types, and parent groupings." },
    { kind: "items", name: "Items", icon: Package,
      desc: "Product & service catalog — rates, tax defaults, and linked accounts." },
    { kind: "invoices", name: "Invoices", icon: FileText,
      desc: "Sales invoices. One row per line item; same Invoice Number groups lines." },
    { kind: "bills", name: "Bills", icon: FileCog,
      desc: "Vendor bills. One row per line item; same Bill Number groups lines." },
    { kind: "sales_receipts", name: "Sales Receipts", icon: Receipt,
      desc: "Cash-sale receipts — imported as immediately paid invoices." },
    { kind: "customer_payments", name: "Customer Payments", icon: DollarSign,
      desc: "Payments applied to existing invoices. Matches by Invoice Number." },
    { kind: "vendor_payments", name: "Vendor Payments", icon: DollarSign,
      desc: "Payments applied to existing bills. Matches by Bill Number." },
];

function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function EntityCard({ entity }) {
    const [busy, setBusy] = useState(null);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);
    const supportsAutoItems = entity.kind === "bills" || entity.kind === "invoices";
    const [autoCreateItems, setAutoCreateItems] = useState(true);

    const download = async (what) => {
        setBusy(what);
        try {
            const orgId = localStorage.getItem("bl_org_id");
            const resp = await axios.get(`${API_BASE}/${what}/${entity.kind}`, {
                withCredentials: true,
                responseType: "blob",
                headers: { "X-Org-Id": orgId || "" },
            });
            const filename = `${entity.kind}_${what}.csv`;
            triggerDownload(resp.data, filename);
            toast.success(`${what === "templates" ? "Template" : "Export"} downloaded`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(null); }
    };

    const handleFile = async (file) => {
        if (!file) return;
        setBusy("import"); setResult(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const orgId = localStorage.getItem("bl_org_id");
            const url = supportsAutoItems
                ? `${API_BASE}/import/${entity.kind}?auto_create_items=${autoCreateItems ? "true" : "false"}`
                : `${API_BASE}/import/${entity.kind}`;
            const { data } = await axios.post(url, fd, {
                withCredentials: true,
                headers: { "X-Org-Id": orgId || "" },
            });
            setResult(data);
            const itemsNote = data.items_created ? ` · ${data.items_created} new item${data.items_created === 1 ? "" : "s"} catalogued` : "";
            toast.success(`Imported ${data.inserted} ${entity.name.toLowerCase()}${itemsNote}`);
        } catch (e) {
            const msg = formatApiError(e);
            setResult({ error: msg });
            toast.error(msg);
        } finally {
            setBusy(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const Icon = entity.icon;
    return (
        <div className="rounded-3xl bg-white border border-sand-200 p-6 shadow-card hover:shadow-card-hover transition-all" data-testid={`data-card-${entity.kind}`}>
            <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-ocean-50 text-secondary grid place-items-center shrink-0">
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-xl text-ocean-700">{entity.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{entity.desc}</p>
                </div>
            </div>
            {supportsAutoItems && (
                <label className="mt-4 flex items-center gap-2 text-xs text-ocean-700 cursor-pointer select-none" data-testid={`${entity.kind}-auto-items-label`}>
                    <input
                        type="checkbox"
                        checked={autoCreateItems}
                        onChange={(e) => setAutoCreateItems(e.target.checked)}
                        className="h-3.5 w-3.5 accent-secondary cursor-pointer"
                        data-testid={`${entity.kind}-auto-items-toggle`}
                    />
                    <span>Auto-create missing items from line descriptions</span>
                </label>
            )}
            <div className="mt-5 grid grid-cols-3 gap-1.5">
                <Button variant="outline" className="rounded-xl border-sand-300 h-9 px-2 text-xs whitespace-nowrap justify-center" onClick={() => download("templates")} disabled={busy !== null} data-testid={`template-${entity.kind}`}>
                    {busy === "templates" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileSpreadsheet className="h-3 w-3 mr-1 shrink-0" /> Template</>}
                </Button>
                <Button variant="outline" className="rounded-xl border-sand-300 h-9 px-2 text-xs whitespace-nowrap justify-center" onClick={() => fileRef.current?.click()} disabled={busy !== null} data-testid={`import-${entity.kind}`}>
                    {busy === "import" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3 w-3 mr-1 shrink-0" /> Import</>}
                </Button>
                <Button className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl h-9 px-2 text-xs whitespace-nowrap justify-center" onClick={() => download("export")} disabled={busy !== null} data-testid={`export-${entity.kind}`}>
                    {busy === "export" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3 w-3 mr-1 shrink-0" /> Export</>}
                </Button>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            {result && (
                <div className="mt-4 rounded-xl bg-sand-100 p-3 text-xs flex items-start gap-2">
                    {result.error ? (
                        <>
                            <AlertCircle className="h-4 w-4 text-coral-dark mt-0.5 shrink-0" />
                            <span className="text-coral-dark">{result.error}</span>
                        </>
                    ) : (
                        <>
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <div className="text-ocean-700 font-medium">Import complete</div>
                                <div className="text-muted-foreground mt-0.5 flex gap-3">
                                    <span>Inserted <b className="text-ocean-700">{result.inserted}</b></span>
                                    <span>Skipped <b>{result.skipped}</b></span>
                                    {result.errors?.length > 0 && <span>Errors <b className="text-coral-dark">{result.errors.length}</b></span>}
                                </div>
                                {result.errors?.length > 0 && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-coral-dark">View errors</summary>
                                        <pre className="mt-1 bg-white p-2 rounded text-[10px] overflow-x-auto">{JSON.stringify(result.errors.slice(0, 10), null, 2)}</pre>
                                    </details>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default function DataHub() {
    const { currentOrg } = useAuth();
    if (!currentOrg) return null;
    return (
        <div>
            <PageHeader
                subtitle="Workspace"
                title="Data import & export"
                action={<Badge className="bg-ocean-50 text-ocean-700 border-0">Zoho Books CSV format supported</Badge>}
            />
            <p className="max-w-2xl text-sm text-muted-foreground mb-6">
                Migrate from your previous platform in minutes. Download a template to see the expected columns, import your exported CSV, or export BlueLedger data any time.
                <span className="block mt-2 text-xs">Tip: for bills and invoices, one CSV row represents one line item. Rows sharing the same Bill/Invoice number are grouped into a single document.</span>
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {ENTITIES.map((e) => <EntityCard key={e.kind} entity={e} />)}
            </div>
        </div>
    );
}
